import React from 'react';
import { Space, Table } from 'antd';
import Modal from '../../../../components/Modal';
import {
  PreviewItem,
  PreviewResponse,
} from '../../../../lib/api/settlementCompleteDateAdjustment';
import {
  ACTIVE_BATCH_COLUMNS,
  APPLIED_COLUMNS,
  EXCLUDED_COLUMNS,
  MONTH_CHANGED_COLUMNS,
} from './columns';
import { StyledSummary, StyledTabs } from './styles';

type Props = {
  open: boolean;
  result: PreviewResponse | null;
  executing?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * 일괄 변경 미리보기 모달.
 *
 * 상단은 건수 요약, 하단은 4개 탭(적용 대상 / 제외 / 월 변경 영향 / 활성 배치 중복).
 *  - 적용 대상 탭: 변경 전/후 completeDate 단일 컬럼으로 비교
 *  - 제외 탭: 제외 사유 한국어 라벨 (nonCompleted / nullCompleteDate / notFound)
 *  - 월 변경 영향 탭: before/after 의 YYYY-MM 이 달라지는 행만 모아 회계 영향 강조
 *  - 활성 배치 중복 탭: 해당 item 이 이미 포함된 다른 배치 ID
 *
 * 기준일 변경의 "영업일 자동 보정" 탭은 이 도메인에는 없음 — 입력값이 보정 없이
 * 그대로 저장되므로 4번째 탭은 "월 변경 영향" 으로 교체한다.
 */
export const PreviewModal = ({
  open,
  result,
  executing,
  onCancel,
  onConfirm,
}: Props): React.JSX.Element => {
  // 탭3 (월 변경 영향) / 탭4 (활성 배치 중복) 은 PreviewItem 의 부분 집합 — 호출처에서 filter.
  const monthChangedItems = result?.items.filter((i) => i.monthChange) ?? [];
  const activeBatchItems = result?.items.filter((i) => i.activeBatchId) ?? [];

  const tabItems = result
    ? [
        {
          key: 'applied',
          label: `적용 대상 (${result.summary.totalCount})`,
          children: (
            <Table<PreviewItem>
              size="small"
              rowKey="id"
              dataSource={result.items}
              columns={APPLIED_COLUMNS}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 'max-content' }}
              bordered
              locale={{ emptyText: '적용 대상이 없습니다.' }}
            />
          ),
        },
        {
          key: 'excluded',
          label: `제외 (${result.summary.excludedCount})`,
          children: (
            <Table<{ id: number; reason: string }>
              size="small"
              rowKey="id"
              dataSource={result.excluded}
              columns={EXCLUDED_COLUMNS}
              pagination={{ pageSize: 20 }}
              bordered
              locale={{ emptyText: '제외된 건이 없습니다.' }}
            />
          ),
        },
        {
          key: 'monthChanged',
          label: `월 변경 영향 (${result.summary.monthChangedCount})`,
          children: (
            <Table<PreviewItem>
              size="small"
              rowKey="id"
              dataSource={monthChangedItems}
              columns={MONTH_CHANGED_COLUMNS}
              pagination={{ pageSize: 20 }}
              bordered
              locale={{
                emptyText: '월 경계가 바뀌는 건이 없습니다.',
              }}
            />
          ),
        },
        {
          key: 'activeBatch',
          label: `활성 배치 중복 (${result.summary.activeBatchCount})`,
          children: (
            <Table<PreviewItem>
              size="small"
              rowKey="id"
              dataSource={activeBatchItems}
              columns={ACTIVE_BATCH_COLUMNS}
              pagination={{ pageSize: 20 }}
              bordered
              locale={{
                emptyText: '다른 활성 배치와 중복되는 건이 없습니다.',
              }}
            />
          ),
        },
      ]
    : [];

  return (
    <Modal
      title="일괄 변경 미리보기"
      isOpen={open}
      width={1200}
      isFooter
      okText={
        executing ? '실행 중...' : result ? `${result.items.length}건 실행` : '실행'
      }
      cancelText="취소"
      onOk={onConfirm}
      onClose={onCancel}
      padding="20px"
      maskClosable={false}
      destroyOnClose
    >
      {result && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <StyledSummary>
            <dl>
              <dt>적용 대상</dt>
              <dd>
                <strong>{result.summary.totalCount}</strong>건
              </dd>
            </dl>
            <dl>
              <dt>제외</dt>
              <dd>{result.summary.excludedCount}건</dd>
            </dl>
            <dl className={result.summary.monthChangedCount > 0 ? 'alert' : ''}>
              <dt>월 경계 변경</dt>
              <dd>{result.summary.monthChangedCount}건</dd>
            </dl>
            <dl className={result.summary.activeBatchCount > 0 ? 'alert' : ''}>
              <dt>활성 배치 중복</dt>
              <dd>{result.summary.activeBatchCount}건</dd>
            </dl>
          </StyledSummary>

          <StyledTabs items={tabItems} defaultActiveKey="applied" />
        </Space>
      )}
    </Modal>
  );
};
